import type {
  FormType,
  VisitCategory,
} from "@/api/form-builder-assignments/formBuilderAssignments.types";
import {
  toFormTypeSlug,
  toWorkOrderTypeSlug,
  type ScopeTarget,
} from "@/components/features/FormBuilder/utils/scopeAssignments";
import { encodeChildKey } from "./scopeTargets";

/** The assignment slot a new form is being created for. */
export interface SlotPrefill {
  name?: string;
  formType: FormType;
  workOrderType: VisitCategory;
  tradeId?: number;
  subtradeId?: number;
  scope: ScopeTarget;
  entityName?: string;
  /** "Entity · Reactive · Check-In" style description shown to the person. */
  label: string;
}

interface Option {
  value: string;
  label: string;
}

export interface SlotPrefillOptions {
  formTypeOptions: Option[];
  workOrderTypeOptions: Option[];
  levelOptions: Option[];
}

/** Why a slot cannot be pre-filled: no dropdown option for it, or no level to scope it to. */
export type SlotIssue = "slot" | "scope";

export interface ResolvedSlotPrefill {
  name: string;
  formTypeId: string;
  workOrderTypeId: string;
  tradeIds: string[];
  subTradeIds: string[];
  levelIds: string[];
  entityIds: string[];
  clientKeys: string[];
  brandKeys: string[];
}

const scopeLevelLabel = ({ clientId, brandId }: ScopeTarget) =>
  brandId ? "brand" : clientId ? "client" : "entity";

// Ids come from the dropdowns the modal already loads, matched by the same label-to-slug
// mapping the assignment payload uses, so nothing here hardcodes a database id.
export function resolveSlotPrefill(
  slot: SlotPrefill,
  { formTypeOptions, workOrderTypeOptions, levelOptions }: SlotPrefillOptions,
): ResolvedSlotPrefill | null {
  const formTypeId = formTypeOptions.find(
    (option) => toFormTypeSlug(option.label) === slot.formType,
  )?.value;
  const workOrderTypeId = workOrderTypeOptions.find(
    (option) => toWorkOrderTypeSlug(option.label) === slot.workOrderType,
  )?.value;
  const levelId = levelOptions.find(
    (option) => option.label.trim().toLowerCase() === scopeLevelLabel(slot.scope),
  )?.value;
  if (!formTypeId || !workOrderTypeId) return null;

  const { tenantId, clientId, brandId } = slot.scope;
  return {
    name: slot.name ?? "",
    formTypeId,
    workOrderTypeId,
    tradeIds: slot.tradeId ? [String(slot.tradeId)] : [],
    subTradeIds: slot.subtradeId ? [String(slot.subtradeId)] : [],
    levelIds: levelId ? [levelId] : [],
    entityIds: levelId ? [tenantId] : [],
    clientKeys: levelId && clientId ? [encodeChildKey(tenantId, clientId)] : [],
    brandKeys: levelId && clientId && brandId ? [encodeChildKey(clientId, brandId)] : [],
  };
}

const sameIds = (a: string[], b: string[]) =>
  a.length === b.length && [...a].sort().join(",") === [...b].sort().join(",");

interface SlotSelection {
  formTypeId: string;
  workOrderTypeId: string;
  tradeIds: string[];
  subTradeIds: string[];
  scopeTargets: ScopeTarget[];
}

/** Whether the form as filled in still lands in the slot it was opened for. */
export function stillMatchesSlot(
  slot: SlotPrefill,
  resolved: ResolvedSlotPrefill,
  selection: SlotSelection,
): boolean {
  return (
    selection.formTypeId === resolved.formTypeId &&
    selection.workOrderTypeId === resolved.workOrderTypeId &&
    sameIds(selection.tradeIds, resolved.tradeIds) &&
    sameIds(selection.subTradeIds, resolved.subTradeIds) &&
    selection.scopeTargets.some(
      (target) =>
        target.tenantId === slot.scope.tenantId &&
        target.clientId === slot.scope.clientId &&
        target.brandId === slot.scope.brandId,
    )
  );
}
